import { useState, useEffect, useCallback } from "react";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import Logo from "@/components/Logo";

const PHONE = "tel:+41766388655";
const WHATSAPP = "https://wa.me/41766388655?text=Bonjour%2C%20je%20souhaite%20un%20devis.";

const NAV_ITEMS = [
  { label: "Services", href: "/services", isRoute: true },
  { label: "Processus", href: "#processus" },
  { label: "Avis", href: "#temoignages" },
  { label: "Blog", href: "/blog", isRoute: true },
  { label: "Contact", href: "/contact", isRoute: true },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToHash = useCallback((hash: string) => {
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + hash);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [location.pathname, navigate]);

  // Scroll to hash after navigation
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "glass-card shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between h-24 md:h-28 px-4">
          {/* Logo */}
          <Logo size="md" />

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((item) =>
              item.isRoute ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.href}
                  onClick={() => scrollToHash(item.href)}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              )
            )}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <Button size="sm" variant="outline" className="gap-2 border-primary text-foreground bg-primary/10 hover:bg-primary/20" asChild>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </Button>
            <Button size="sm" className="gap-2" asChild>
              <a href={PHONE}>
                <Phone className="w-4 h-4" /> Demander un devis
              </a>
            </Button>
          </div>

          {/* Mobile menu toggle */}
          <button className="md:hidden text-foreground relative z-50" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}>
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden backdrop-blur-xl bg-background/80 animate-fade-in">
          <nav className="flex flex-col items-center justify-center h-full gap-8">
            {NAV_ITEMS.map((item, i) =>
              item.isRoute ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-2xl font-bold text-foreground hover:text-primary transition-colors animate-fade-in"
                  style={{ animationDelay: `${i * 80}ms`, animationFillMode: "both" }}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.href}
                  className="text-2xl font-bold text-foreground hover:text-primary transition-colors animate-fade-in"
                  style={{ animationDelay: `${i * 80}ms`, animationFillMode: "both" }}
                  onClick={() => scrollToHash(item.href)}
                >
                  {item.label}
                </button>
              )
            )}

            {/* CTA buttons */}
            <div className="flex flex-col gap-4 mt-8 animate-fade-in items-center" style={{ animationDelay: `${NAV_ITEMS.length * 80}ms`, animationFillMode: "both" }}>
              <ThemeToggle />
              <Button size="lg" variant="outline" className="gap-2 border-primary/30 text-primary hover:bg-primary/10" asChild>
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              </Button>
              <Button size="lg" className="gap-2 glow-sm" asChild>
                <a href={PHONE}>
                  <Phone className="w-5 h-5" /> Demander un devis
                </a>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
